// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Line, Bar } from 'react-chartjs-2';
import { Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '../ui/label';
import { EducBox, ChartBox, ResultCard, InputRow, PremiumLock, EDUC, baseChartOpts, barLabelPlugin, fmt } from './shared';

const ShareBtn = ({ text }) => {
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ text, url: 'https://finsapiens.it' }); } catch {}
    } else {
      try { await navigator.clipboard.writeText(text + '\nhttps://finsapiens.it'); toast.success('Copiato negli appunti ✓'); } catch { toast.error('Errore copia'); }
    }
  };
  return (
    <button onClick={share} data-testid="affitto-share-btn" className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg transition-colors hover:bg-white/[0.04]" style={{ border: '1px solid rgba(201,168,76,0.4)', color: '#C9A84C' }}>
      <Share2 className="w-3 h-3" />Condividi il risultato
    </button>
  );
};

const AffittoVsMutuoCalc = () => {
  const [prezzo, setPrezzo] = useState('250000');
  const [anticipo, setAnticipo] = useState('50000');
  const [tasso, setTasso] = useState('3.5');
  const [durata, setDurata] = useState('25');
  const [speseAcq, setSpeseAcq] = useState('8000');
  const [manutenzione, setManutenzione] = useState('1500');
  const [speseStr, setSpeseStr] = useState('15000');
  const [freqStr, setFreqStr] = useState('15');
  const [imuOn, setImuOn] = useState(false);
  const [valCatastale, setValCatastale] = useState('100000');
  const [tari, setTari] = useState('300');
  const [rivalutazione, setRivalutazione] = useState('1.5');
  const [canone, setCanone] = useState('900');
  const [adeguamento, setAdeguamento] = useState('2.0');
  const [speseCondom, setSpeseCondom] = useState('80');
  const [deposito, setDeposito] = useState('1800');
  const [capitaleAlt, setCapitaleAlt] = useState('50000');
  const [rendimento, setRendimento] = useState('6');
  const [orizzonte, setOrizzonte] = useState(20);

  useEffect(() => { setCapitaleAlt(anticipo); }, [anticipo]);

  const P = parseFloat(prezzo) || 0;
  const A = parseFloat(anticipo) || 0;
  const T = parseFloat(tasso) || 0;
  const D = parseInt(durata) || 0;
  const SA = parseFloat(speseAcq) || 0;
  const MAN = parseFloat(manutenzione) || 0;
  const SS = parseFloat(speseStr) || 0;
  const FS = parseInt(freqStr) || 15;
  const VC = parseFloat(valCatastale) || 0;
  const TARI_V = parseFloat(tari) || 0;
  const RIV = parseFloat(rivalutazione) || 0;
  const CAN = parseFloat(canone) || 0;
  const ADJ = parseFloat(adeguamento) || 0;
  const SC = parseFloat(speseCondom) || 0;
  const DEP = parseFloat(deposito) || 0;
  const CAP = parseFloat(capitaleAlt) || A;
  const REND = parseFloat(rendimento) || 0;
  const N = orizzonte;

  const P_mutuo = Math.max(0, P - A);
  const n_tot = D * 12;
  const r_month = T / 100 / 12;
  const rata = n_tot > 0 && r_month > 0
    ? P_mutuo * (r_month * Math.pow(1 + r_month, n_tot)) / (Math.pow(1 + r_month, n_tot) - 1)
    : n_tot > 0 ? P_mutuo / n_tot : 0;
  const mo_paid = Math.min(N * 12, n_tot);
  const debito_residuo = r_month > 0 && n_tot > 0
    ? Math.max(0, P_mutuo * Math.pow(1 + r_month, mo_paid) - rata * (Math.pow(1 + r_month, mo_paid) - 1) / r_month)
    : Math.max(0, P_mutuo - (P_mutuo / Math.max(1, n_tot)) * mo_paid);
  const val_immobile = P * Math.pow(1 + RIV / 100, N);
  const imu_annua = imuOn ? VC * 0.0086 : 0;
  const costo_totale_mutuo = rata * mo_paid + SA + MAN * N + (FS > 0 ? SS * (N / FS) : 0) + imu_annua * N + TARI_V * N;
  const patrimonio_mutuo = val_immobile - debito_residuo;

  let total_rent = 0;
  for (let y = 0; y < N; y++) total_rent += CAN * 12 * Math.pow(1 + ADJ / 100, y);
  const cond_cumul = SC * 12 * N;
  const costo_opp_dep = DEP * (Math.pow(1 + REND / 100, N) - 1);
  const costo_totale_affitto = total_rent + cond_cumul + costo_opp_dep;
  const cap_cresciuto = CAP * Math.pow(1 + REND / 100, N);
  const patrimonio_affitto = cap_cresciuto - costo_totale_affitto;
  const differenza = Math.abs(patrimonio_mutuo - patrimonio_affitto);
  const favore = patrimonio_mutuo > patrimonio_affitto ? 'mutuo' : 'affitto';

  const chartLabels = Array.from({ length: N + 1 }, (_, i) => i % 5 === 0 ? `Anno ${i}` : '');
  const dataMutuo = [], dataAffitto = [];
  for (let y = 0; y <= N; y++) {
    const mo_y = Math.min(y * 12, n_tot);
    dataMutuo.push(Math.round(rata * mo_y + SA + MAN * y + (FS > 0 ? SS * (y / FS) : 0) + imu_annua * y + TARI_V * y));
    let rent_y = 0;
    for (let yr = 0; yr < y; yr++) rent_y += CAN * 12 * Math.pow(1 + ADJ / 100, yr);
    dataAffitto.push(Math.round(rent_y + SC * 12 * y + DEP * (Math.pow(1 + REND / 100, y) - 1)));
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ position: 'relative' }}>
      <EducBox text={EDUC.affitto} />
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="sapiens-card p-5">
          <h3 className="text-base font-semibold text-[#E8E8E4] mb-4">🏠 Scenario Mutuo</h3>
          <div className="space-y-3">
            <InputRow label="Prezzo immobile (€)" value={prezzo} onChange={setPrezzo} prefix="€" />
            <InputRow label="Anticipo versato (€)" value={anticipo} onChange={setAnticipo} prefix="€" tooltip="Riduce il capitale del mutuo ma immobilizza liquidità" />
            <InputRow label="Tasso mutuo annuo (%)" value={tasso} onChange={setTasso} suffix="%" />
            <InputRow label="Durata mutuo (anni)" value={durata} onChange={setDurata} suffix="anni" />
            <InputRow label="Spese acquisto (€)" value={speseAcq} onChange={setSpeseAcq} prefix="€" tooltip="Notaio, imposta registro, perizia, istruttoria" />
            <InputRow label="Manutenzione ordinaria annua (€)" value={manutenzione} onChange={setManutenzione} prefix="€" />
            <div className="grid grid-cols-2 gap-2">
              <InputRow label="Spese straordinarie (€)" value={speseStr} onChange={setSpeseStr} prefix="€" />
              <InputRow label="Ogni (anni)" value={freqStr} onChange={setFreqStr} suffix="anni" />
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setImuOn(!imuOn)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${imuOn ? 'bg-[#C9A84C] text-[#3A2800]' : 'bg-[#252523] text-[#888780] border border-white/[0.08]'}`}>IMU seconda casa</button>
              {imuOn && <InputRow label="Valore catastale (€)" value={valCatastale} onChange={setValCatastale} prefix="€" />}
            </div>
            <InputRow label="TARI annua (€)" value={tari} onChange={setTari} prefix="€" />
            <InputRow label="Rivalutazione immobile (%/anno)" value={rivalutazione} onChange={setRivalutazione} suffix="%" tooltip="Media storica italiana: 1-2%/anno" />
          </div>
        </div>
        <div className="sapiens-card p-5">
          <h3 className="text-base font-semibold text-[#E8E8E4] mb-4">🔑 Scenario Affitto</h3>
          <div className="space-y-3">
            <InputRow label="Canone mensile (€)" value={canone} onChange={setCanone} prefix="€" />
            <InputRow label="Adeguamento ISTAT (%/anno)" value={adeguamento} onChange={setAdeguamento} suffix="%" tooltip="La maggior parte dei contratti 4+4 prevede adeguamento annuo al 75% dell'inflazione" />
            <InputRow label="Spese condominiali mensili (€)" value={speseCondom} onChange={setSpeseCondom} prefix="€" />
            <InputRow label="Deposito cauzionale (€)" value={deposito} onChange={setDeposito} prefix="€" tooltip="Liquidità immobilizzata per tutta la durata dell'affitto" />
            <InputRow label="Capitale alternativo investito (€)" value={capitaleAlt} onChange={setCapitaleAlt} prefix="€" tooltip="Se non compri casa puoi investire l'anticipo" />
            <InputRow label="Rendimento investimento (%/anno)" value={rendimento} onChange={setRendimento} suffix="%" tooltip="Rendimento atteso investendo l'anticipo in ETF diversificati. Media MSCI World ~7%/anno" />
          </div>
        </div>
      </div>
      <div className="sapiens-card p-4 mb-6">
        <Label className="text-[#888780] text-sm mb-3 block">Orizzonte temporale</Label>
        <div className="flex gap-2 flex-wrap">
          {[10, 15, 20, 25].map(y => (
            <button key={y} onClick={() => setOrizzonte(y)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${orizzonte === y ? 'text-[#3A2800]' : 'bg-[#252523] text-[#888780] border border-white/[0.08]'}`} style={orizzonte === y ? { background: '#C9A84C' } : {}} data-testid={`orizzonte-${y}`}>
              {y} anni
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <ResultCard label={`Costo totale mutuo in ${N} anni`} value={`€${fmt(costo_totale_mutuo)}`} />
        <ResultCard label={`Costo totale affitto in ${N} anni`} value={`€${fmt(costo_totale_affitto)}`} />
        <ResultCard label="Differenza" value={`€${fmt(differenza)}`} gold sub={favore === 'mutuo' ? 'A favore del Mutuo' : "A favore dell'Affitto"} />
        <ResultCard label={`Valore immobile tra ${N} anni`} value={`€${fmt(val_immobile)}`} gold />
        <ResultCard label="Patrimonio netto — Mutuo" value={`€${fmt(patrimonio_mutuo)}`} green={patrimonio_mutuo > patrimonio_affitto} />
        <ResultCard label="Patrimonio netto — Affitto" value={`€${fmt(patrimonio_affitto)}`} green={patrimonio_affitto > patrimonio_mutuo} />
      </div>
      <div className="p-4 rounded-xl mb-6" style={{ background: favore === 'mutuo' ? 'rgba(61,122,82,0.1)' : 'rgba(201,168,76,0.08)', border: `1px solid ${favore === 'mutuo' ? '#3D7A52' : 'rgba(201,168,76,0.4)'}` }}>
        <p className="text-sm text-[#E8E8E4]">
          {favore === 'mutuo'
            ? `In questo scenario acquistare conviene. Dopo ${N} anni il patrimonio netto è maggiore di €${fmt(differenza)} rispetto all'affitto.`
            : `In questo scenario affittare conviene. La differenza di €${fmt(differenza)} potrebbe essere investita per costruire patrimonio in modo alternativo.`}
        </p>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <ChartBox title="Costo cumulativo nel tempo" height={440}>
          <Line
            data={{ labels: chartLabels, datasets: [
              { label: 'Scenario Mutuo', data: dataMutuo, borderColor: '#3D7A52', backgroundColor: 'rgba(61,122,82,0.3)', fill: true, tension: 0.3, pointRadius: 0 },
              { label: 'Scenario Affitto', data: dataAffitto, borderColor: '#C9A84C', backgroundColor: 'rgba(201,168,76,0.3)', fill: true, tension: 0.3, pointRadius: 0 },
            ] }}
            options={{ ...baseChartOpts(), plugins: { ...baseChartOpts().plugins, legend: { display: true, labels: { color: '#888780' } } } }}
          />
        </ChartBox>
        <ChartBox title="Patrimonio netto a fine periodo" height={320}>
          <Bar
            data={{ labels: ['Scenario Mutuo', 'Scenario Affitto'], datasets: [{ data: [Math.round(patrimonio_mutuo), Math.round(patrimonio_affitto)], backgroundColor: ['rgba(61,122,82,0.7)', 'rgba(201,168,76,0.7)'], borderRadius: 8, borderSkipped: false }] }}
            options={baseChartOpts()}
            plugins={[barLabelPlugin]}
          />
        </ChartBox>
      </div>
      <p className="text-xs text-[#888780] mt-4 p-3 bg-[#252523] rounded-xl">
        I calcoli sono stime educative basate sulle ipotesi inserite. Rivalutazione immobiliare, inflazione, tassi e rendimenti variano nel tempo. Prima di una decisione immobiliare consulta un consulente finanziario indipendente.
      </p>
      <div className="mt-3 flex justify-end">
        <ShareBtn text={`Nel mio caso ${patrimonio_mutuo > patrimonio_affitto ? 'il mutuo' : "l'affitto"} conviene di €${fmt(Math.abs(patrimonio_mutuo - patrimonio_affitto))} su ${parseInt(orizzonte)} anni 🏠`} />
      </div>
      <div style={{ position: 'absolute', inset: 0 }}><PremiumLock /></div>
</motion.div>
  );
};

export default AffittoVsMutuoCalc;
